import { ButtonNew, LoadingWrapper, LuneThemeNew } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { notify } from 'bugsnag'
import { updateRfqTemplate } from 'endpoints/dapi'
import { useLlmValueMapping } from 'hooks/useLlmValueMapping'
import { FileDetails } from 'views/CalculateEmissions/RFQ/RFQ'

import {
    MissingMappingData,
    PARAMETER_MAPPING_OUTPUT,
    parameterMappingOutputFormatter,
    TypedDapiRfqTemplate,
} from '../utils/types'
import {
    findMappingsInTemplate,
    getValueMappingFromTemplate,
    hasValueMapping,
    inferValueMapping,
    validateSheetWithMapping,
} from '../utils/utils'

import InfoMessage from './InfoMessage'
import { showMapValuesModal } from './MapValuesModal'

const { palette } = LuneThemeNew

const UnmappedProperties: FC<{
    unmappedProperties: MissingMappingData[]
    mapping: TypedDapiRfqTemplate
    fileDetails: FileDetails
    uploadDataSheet: () => Promise<{
        result: 'error' | 'success'
        info: { subject: string[]; description?: string }
    }>
    onClose: () => void
}> = ({ unmappedProperties, fileDetails, uploadDataSheet, onClose, mapping }) => {
    const navigate = useNavigate()
    const [errorsModal, setErrorsModal] = useState<
        { subject: string[]; description?: string } | undefined
    >(undefined)
    const [isUploadSuccess, setIsUploadSuccess] = useState(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string>()
    const [isComplete, setIsComplete] = useState<boolean>()
    const [uploading, setUploading] = useState<boolean>()
    const { template, id, name } = mapping

    const calculateButton = (
        <ButtonNew
            loading={uploading}
            onClick={async () => {
                if (uploading) return
                setUploading(true)
                try {
                    const uploadResult = await uploadDataSheet()
                    if (uploadResult.result === 'success') {
                        setIsUploadSuccess(true)
                        setErrorsModal(uploadResult.info)
                    } else {
                        setIsUploadSuccess(false)
                        setErrorsModal(uploadResult.info)
                    }
                } finally {
                    setUploading(false)
                }
            }}
        >
            {isComplete ? 'Calculate emissions' : 'Calculate with missing values'}
        </ButtonNew>
    )

    async function startMapping(unmappedProperties: MissingMappingData[]) {
        setLoading(true)
        // Wait for LLM inference results and use them if successful
        const llmResult = (await useLlmValueMapping(unmappedProperties)).unwrapOrElse((error) => {
            notify(new Error(`LLM value mapping inference failed: ${error.message}`))
            return undefined
        })
        setLoading(false)

        for (const unmappedProperty of unmappedProperties) {
            const { name, legIndex, unmappedValues } = unmappedProperty
            const possibleValueMapping = getValueMappingFromTemplate(template, unmappedProperty)
            if (possibleValueMapping.isNone()) {
                throw new Error(
                    `Programming error: Expected a column value mapping in ${name} property in ${unmappedProperty.legProperty} in leg ${legIndex}}`,
                )
            }
            const valueMapping = possibleValueMapping.value
            const llmMapping = llmResult?.valueMappings.find(
                (mapping) => mapping.propertyName === name,
            )
            const selection = await showMapValuesModal({
                label: name,
                outputValues: PARAMETER_MAPPING_OUTPUT[name],
                formatOutput: parameterMappingOutputFormatter[name],
                mappings:
                    llmMapping !== undefined
                        ? llmMapping.valueMapping.map(({ spreadsheetValue, destinationValue }) => [
                              spreadsheetValue,
                              destinationValue,
                          ])
                        : // Fallback to regular inference when there's no DAPI value mapping for a property
                          inferValueMapping<(typeof PARAMETER_MAPPING_OUTPUT)[typeof name][number]>(
                              unmappedValues,
                              PARAMETER_MAPPING_OUTPUT[name],
                              name,
                          ),
                isTradelane: name === 'tradelane',
                isTransportMode: name === 'mode',
                isCountryCode: name === 'countryCode',
                autoFill: true,
            })

            if (selection) {
                findMappingsInTemplate(template, unmappedProperty).forEach((target) => {
                    if (!hasValueMapping(target)) {
                        throw new Error(
                            `Programming error. Provided property (${name} property in ${unmappedProperty.legProperty} in leg ${legIndex}}) does not have value mapping information`,
                        )
                    }
                    target.valueMapping.mappings = valueMapping.concat(selection)
                })
            } else {
                setError(` No mapping selection made for ${name} property on leg ${legIndex}`)
                return
            }
        }
        const revalidation = await validateSheetWithMapping(fileDetails, template)
        // Modifying transport mode mapping might trigger new mapping issues to appear.
        // Resolve them if they exist. This can only happen once with current logic
        if (
            revalidation !== 'new_template_required' &&
            revalidation.missingMappingProperties.length
        ) {
            return await startMapping(revalidation.missingMappingProperties)
        }
        await updateRfqTemplate(id, name, template)
        setIsComplete(true)
    }

    return (
        <>
            {errorsModal && (
                <InfoMessage
                    subject={errorsModal.subject}
                    description={errorsModal.description}
                    onClose={() => {
                        setErrorsModal(undefined)
                        onClose()
                        if (isUploadSuccess) {
                            navigate('/imports')
                        }
                    }}
                />
            )}
            <LoadingWrapper loading={loading} sx={{ minHeight: '200px', width: '100%' }}>
                {isComplete ? (
                    <Text
                        variant={'h5'}
                        textAlign={'center'}
                    >{`All values successfully mapped`}</Text>
                ) : (
                    <Text
                        variant={'h5'}
                        textAlign={'center'}
                    >{`File contains unmapped values`}</Text>
                )}
                {!isComplete ? (
                    <Text
                        variant={'body3'}
                        textAlign={'center'}
                    >{`We've detected columns that have values that have not yet been mapped to a Lune field`}</Text>
                ) : undefined}
                {error ? (
                    <Text variant={'body2'} textAlign={'center'}>
                        <span style={{ color: palette.TextError }}>Error:</span>
                        {error}
                    </Text>
                ) : undefined}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 2,
                        width: '100%',
                        button: {
                            maxWidth: 'max-content',
                        },
                    }}
                >
                    {!isComplete && (
                        <ButtonNew
                            disabled={loading}
                            loading={loading}
                            style={'outline'}
                            onClick={() => startMapping(unmappedProperties)}
                        >
                            Add new values
                        </ButtonNew>
                    )}
                    {calculateButton}
                </Box>
            </LoadingWrapper>
        </>
    )
}

export default UnmappedProperties
